// ---------------------------------------------------------------------------
// Sales guides - the lane that fills the desks.
//
// Leads live in `leads_management`, which is entirely separate from Members.
// A lead is somebody who might buy. A member is somebody who did. The whole
// point of this lane is the crossing between the two, and the last guide is
// about nothing else.
//
// Every route in this module is gated on `leads.view` except the proposal
// builder, which needs `leads.manage`. Since each guide here ends up asking the
// user to create or send something, all four require both codes, and
// journeys.py says the same.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const LEADS = PATH_ADMIN.leadsManagement.list;
const MY_DAY = PATH_ADMIN.leadsManagement.myDay;
const FOLLOW_UPS = PATH_ADMIN.leadsManagement.followUps;
const PROPOSALS = PATH_ADMIN.leadsManagement.proposals;
const CREATE_PROPOSAL = PATH_ADMIN.leadsManagement.createProposal;

const SALES_PERMISSION = ['leads.view', 'leads.manage'];

export const SALES_LEADS = {
  id: 'sales-leads',
  title: 'Capture and qualify leads',
  summary:
    'Where enquiries come from, what the pipeline stages mean, and how to add '
    + 'one properly so it does not go cold.',
  category: 'sales',
  level: 'advanced',
  permission: SALES_PERMISSION,
  featureSlug: null,
  // The Add Lead button lives in the desktop header only, so on a phone this
  // guide would dead-end two steps in. Better to not offer it there at all.
  desktopOnly: true,
  estMinutes: 6,
  readinessKey: null,
  keywords: [
    'lead', 'leads', 'enquiry', 'inquiry', 'pipeline', 'add lead', 'crm',
    'qualify', 'prospect', 'deal',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'Leads are people who have not bought yet',
      body:
        'They are kept completely apart from Members on purpose. Nothing here is '
        + 'billed, nothing holds a seat, and nothing shows up in occupancy. A '
        + 'lead becomes a member at exactly one moment, when you convert them, '
        + 'and that is the last guide in this lane.',
    },
    {
      id: 'sources',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'Most of these should arrive on their own',
      body:
        'Your website form, the aggregator listings you are on, ad campaigns and '
        + 'walk-ins can all land straight in this list. If your team is still '
        + 'typing enquiries in from a shared inbox, that is worth fixing before '
        + 'anything else in this lane, because the leads people forget to type '
        + 'in are the ones you never find out you lost.',
    },
    {
      id: 'add',
      kind: 'act',
      route: LEADS,
      target: ANCHOR.leadsAddNew,
      placement: 'bottom',
      title: 'Add one by hand',
      body:
        'Use this for the phone call and the person who walked in off the '
        + 'street. Add it while they are still in front of you.',
      waitingLabel: 'Click Add Lead.',
      advanceOn: [{ type: 'click', target: ANCHOR.leadsAddNew }],
      doItForMe: { type: 'click', target: ANCHOR.leadsAddNew },
      allowSkip: true,
    },
    {
      id: 'fields',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Three fields decide whether this lead survives',
      body:
        'How many seats they need, when they want to move in, and who owns the '
        + 'lead. Requirement and date tell you whether you can even serve them. '
        + 'The owner is who is accountable, and an unowned lead is a lead nobody '
        + 'calls. The source matters too, because in a few months it is what '
        + 'tells you which channel is worth paying for.',
      waitingLabel: 'Waiting for the lead to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.leadCreated }],
      allowSkip: true,
    },
    {
      id: 'stages',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'The stages are a forecast, so keep them honest',
      body:
        'New, contacted, toured, proposal sent, won or lost. Moving a lead '
        + 'forward because you feel good about it rather than because something '
        + 'happened turns your pipeline into fiction. Mark the dead ones lost '
        + 'with a reason. A clean list of ten real leads beats a hopeful list of '
        + 'ninety, and the loss reasons are what tell you whether you are losing '
        + 'on price or on availability.',
    },
    {
      id: 'views',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'List, board and deals',
      body:
        'The same leads, three ways. The board view is the pipeline as columns '
        + 'and is what most people run their day from, and you can drag a card '
        + 'between stages. The deals filter narrows to the ones close enough to '
        + 'be worth a proposal. Open any lead to see its full history: every '
        + 'call, note, tour and proposal in one column.',
    },
  ],
};

export const SALES_FOLLOWUPS = {
  id: 'sales-followups',
  title: 'Never drop a follow-up',
  summary:
    'The daily queue, how reminders are created, and why logging what happened '
    + 'is the whole job.',
  category: 'sales',
  level: 'advanced',
  permission: SALES_PERMISSION,
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'follow up', 'followup', 'reminder', 'my day', 'task', 'call back',
    'overdue', 'activity', 'log a call',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: MY_DAY,
      target: CENTER,
      placement: 'center',
      title: 'This page is your day',
      body:
        'What is due today, what is overdue, and what is coming. If you open one '
        + 'page in DeskOS each morning, make it this one. Deals are almost never '
        + 'lost to a competitor with a better space. They are lost to nobody '
        + 'calling back.',
    },
    {
      id: 'queue',
      kind: 'narrate',
      route: FOLLOW_UPS,
      target: CENTER,
      placement: 'center',
      title: 'Everything owed to a lead, in one queue',
      body:
        'Overdue sorts to the top, because an overdue follow-up is a lead going '
        + 'cold while you look at something else. You can work straight from '
        + 'here without opening each lead, which is the difference between '
        + 'clearing this list and avoiding it.',
    },
    {
      id: 'logging',
      kind: 'narrate',
      route: FOLLOW_UPS,
      target: CENTER,
      placement: 'center',
      title: 'Log what happened, then set the next one',
      body:
        'A follow-up is not finished when you make the call. It is finished when '
        + 'you write down what was said and book the next step. The lead history '
        + 'is what lets a colleague pick this up when you are on leave, and what '
        + 'stops the same customer being asked the same question twice.',
    },
    {
      id: 'auto',
      kind: 'narrate',
      route: FOLLOW_UPS,
      target: CENTER,
      placement: 'center',
      title: 'Some of these schedule themselves',
      body:
        'Depending on how your organization has set up its cadence, moving a '
        + 'lead to a new stage can create the next follow-up for you, so a fresh '
        + 'enquiry never sits with nothing scheduled against it. You can always '
        + 'add, move or cancel one by hand.',
    },
    {
      id: 'team',
      kind: 'narrate',
      route: MY_DAY,
      target: CENTER,
      placement: 'center',
      title: 'If you run the team',
      body:
        'There is a team view showing each person\'s day and how their queue is '
        + 'moving, so you can see who is buried and who is coasting before it '
        + 'shows up in the month\'s numbers. It needs the team permission, so it '
        + 'may not be on your menu.',
    },
  ],
};

export const SALES_PROPOSALS = {
  id: 'sales-proposals',
  title: 'Build and send a proposal',
  summary:
    'Turn a qualified lead into a priced document the customer can read, '
    + 'accept, and be chased on.',
  category: 'sales',
  level: 'advanced',
  permission: SALES_PERMISSION,
  featureSlug: null,
  estMinutes: 7,
  readinessKey: null,
  keywords: [
    'proposal', 'quote', 'quotation', 'send proposal', 'pricing', 'offer',
    'accept', 'proposal template',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PROPOSALS,
      target: CENTER,
      placement: 'center',
      title: 'A proposal is the priced version of the conversation',
      body:
        'What they get, what it costs, for how long. It goes out as a web page '
        + 'and a PDF, and DeskOS tells you when it was opened. Everything you '
        + 'have sent lives on this page with its current state, so "did we ever '
        + 'send them anything" is a five second question.',
    },
    {
      id: 'create',
      kind: 'act',
      route: PROPOSALS,
      target: ANCHOR.proposalsCreate,
      placement: 'bottom',
      title: 'Start one',
      body:
        'You will be asked which lead it is for, which is why qualifying '
        + 'properly earlier pays off here: the requirement and move-in date carry '
        + 'straight through.',
      waitingLabel: 'Click to create a proposal.',
      advanceOn: [
        { type: 'click', target: ANCHOR.proposalsCreate },
        { type: 'route', path: CREATE_PROPOSAL },
      ],
      doItForMe: { type: 'click', target: ANCHOR.proposalsCreate },
      allowSkip: true,
    },
    {
      id: 'build',
      kind: 'narrate',
      route: CREATE_PROPOSAL,
      target: CENTER,
      placement: 'center',
      title: 'Price it against real space',
      body:
        'Pick the space and the plan, and the pricing comes from what you '
        + 'actually sell rather than from a number somebody remembers. Discounts '
        + 'and extras like parking or extra meeting-room credits go on as their '
        + 'own lines, so the customer can see what they are paying for and you '
        + 'can see what you gave away.',
    },
    {
      id: 'terms',
      kind: 'narrate',
      route: CREATE_PROPOSAL,
      target: CENTER,
      placement: 'center',
      title: 'Then the terms, and an expiry',
      body:
        'Deposit, lock-in, notice period and start date. Always set an expiry '
        + 'date. It is the only polite way to create urgency, and it stops a '
        + 'quote from six months ago being waved at you when your rates have '
        + 'moved. The whole document is built from your organization\'s template, '
        + 'so branding and standard terms are already in it.',
    },
    {
      id: 'send',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Send it',
      body:
        'The customer gets a link to a page they can read on a phone, plus the '
        + 'PDF. You will see when they open it, which is a far better prompt to '
        + 'call than a guess. They can accept it online, and an accepted proposal '
        + 'carries its pricing into the order when you convert.',
      waitingLabel: 'Waiting for the proposal to be sent.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.proposalSent }],
      allowSkip: true,
    },
    {
      id: 'chase',
      kind: 'narrate',
      route: PROPOSALS,
      target: CENTER,
      placement: 'center',
      title: 'And then chase it',
      body:
        'Sent is not won. Book the follow-up now, while you remember what you '
        + 'promised. If the terms change during the negotiation, send a revised '
        + 'proposal rather than agreeing something over email that no record '
        + 'anywhere reflects.',
    },
  ],
};

export const SALES_CONVERSION = {
  id: 'sales-conversion',
  title: 'Convert a lead into a member',
  summary:
    'The crossing from sales into operations: what converting actually creates, '
    + 'and what still needs doing after it.',
  category: 'sales',
  level: 'advanced',
  permission: SALES_PERMISSION,
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'convert', 'conversion', 'won', 'close deal', 'lead to member',
    'onboard', 'new customer', 'move in',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'One moment turns a prospect into a customer',
      body:
        'Converting a lead creates the company and the member records on the '
        + 'operations side and links them back to where they came from. Do it '
        + 'from the lead rather than typing the customer in fresh under Members, '
        + 'or you lose the entire history of how you won them.',
    },
    {
      id: 'before',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'Check three things first',
      body:
        'The company name is spelled the way it needs to appear on an invoice. '
        + 'The billing contact is the person who actually pays, not the person '
        + 'you have been getting on with. And the seats you promised are still '
        + 'free for the start date you promised, because a signed deal against a '
        + 'desk that got sold last week is the most expensive mistake in this '
        + 'whole system.',
    },
    {
      id: 'what-happens',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'What conversion does, and what it does not',
      body:
        'It creates the company and the member, and marks the lead won so it '
        + 'leaves your pipeline and counts in your conversion numbers. What it '
        + 'does not do is sell anything. There is still no subscription, no seat '
        + 'held and no invoice. Nothing is occupied and nothing is owed until '
        + 'somebody creates the subscription.',
    },
    {
      id: 'the-order',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'So the sale is not done until the order is',
      body:
        'Create the subscription against the new company using the plan, seats, '
        + 'price and dates from the proposal you sent. That is the step that '
        + 'reserves the desks, raises the first invoice and makes your occupancy '
        + 'true. Whether you do it or hand it to whoever runs memberships, agree '
        + 'inside your team which it is, because this is the handover deals fall '
        + 'through.',
    },
    {
      id: 'measure',
      kind: 'narrate',
      route: LEADS,
      target: CENTER,
      placement: 'center',
      title: 'And it feeds everything you learn next',
      body:
        'Because the won lead keeps its source, its stages and its dates, you '
        + 'can see which channels actually produce paying customers, how long the '
        + 'crossing takes, and where deals stall. None of that exists if people '
        + 'close deals by typing customers straight into Members, which is the '
        + 'real reason this guide exists.',
    },
  ],
};

export default [
  SALES_LEADS,
  SALES_FOLLOWUPS,
  SALES_PROPOSALS,
  SALES_CONVERSION,
];
